// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Tooltip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

// import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

import Editor from "react-simple-wysiwyg";

function PayLinks() {
  const pageTitle = "Pay Links";
  usePageTitle(pageTitle);

  // const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [paylinks, setPaylinks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [openNewCheckDialog, setOpenNewCheckDialog] = useState(false);
  const [amountLabel, setAmountLabel] = useState("Amount");
  const [selectedCurrency, setCurrency] = useState("");
  const [paylinkAmount, setAmount] = useState(0);
  const [paylinkTitle, setTitle] = useState(null);
  const [feeBearer, setFeeBearer] = useState(null);
  const [description, setDescription] = useState(null);
  const [oneTime, setOneTime] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [payer, setPayer] = useState(null);
  const [payerEmail, setPayerEmail] = useState(null);

  useEffect(() => {
    setAmountLabel(`${selectedCurrency} Amount`);
  }, [selectedCurrency]);

  function formatAmount(amount, currency) {
    const prefix = amount < 0 ? "-" : "";
    amount = Math.abs(amount);
    return `${prefix}${currency}${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  async function disablePaylink(reference, paystatus) {
    setShowLoading(true);
    const json = {
      reference: `${reference}`,
      status: !paystatus,
    };

    await axios
      .post("paylinks/disable.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred.");
        setOpen(true);
      });
  }

  const tableColumns = [
    // { Header: "Currency", accessor: "currency", align: "left" },
    { Header: "Preview", accessor: "preview", align: "left" },
    { Header: "Amount", accessor: "amount", align: "center" },
    { Header: "Title", accessor: "title", align: "center" },
    { Header: "Fee Bearer", accessor: "fee_bearer", align: "center" },
    { Header: "Pay Link", accessor: "link", align: "center" },
    { Header: "Usage", accessor: "one_time", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Created", accessor: "created", align: "right" },
  ];

  const buildRateRows = (data) => {
    const rows = [];
    data.map((paylink) => {
      let statusColor = "warning";
      if (paylink.status) {
        statusColor = "success";
      } else {
        statusColor = "error";
      }

      rows.push({
        preview: (
          <a href={paylink.link} target="_blank" rel="noreferrer">
            <MDBadge badgeContent="PREVIEW" variant="contained" container color="info" />
          </a>
        ),
        amount: <b>{formatAmount(paylink.amount / 100, paylink.currency.symbol)}</b>,
        title: paylink.title,
        fee_bearer: paylink.fee_bearer,
        link: (
          <MDButton
            onClick={() => {
              navigator.clipboard.writeText(paylink.link);
              setSeverity("success");
              setErrMsg(`Link copied!`);
              setOpen(true);
            }}
            size="small"
            color="warning"
            variant="contained"
          >
            <span sx={{ pl: 1, textTransform: "capitalize" }}>Copy Link</span>
            &nbsp;
            <Icon>copy</Icon>
          </MDButton>
        ),
        one_time: paylink.is_one_time ? "One Time" : "Forever",
        status: (
          <MDBox>
            <MDBadge
              badgeContent={paylink.status ? "ACTIVE" : "DISABLED"}
              variant="contained"
              container
              color={statusColor}
            />
            {paylink.status || (!paylink.status && !paylink.is_one_time) ? (
              <Tooltip
                title={paylink.status ? "Disable PayLink" : "Enable PayLink"}
                placement="top"
              >
                <MDBox mt={1} variant="button">
                  <MDButton
                    size="small"
                    variant="outlined"
                    color={paylink.status ? "error" : "success"}
                    iconOnly
                    circular
                    onClick={() => {
                      disablePaylink(paylink.reference, paylink.status);
                    }}
                  >
                    <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                      {paylink.status ? "toggle_off" : "toggle_on"}
                    </Icon>
                  </MDButton>
                </MDBox>
              </Tooltip>
            ) : (
              <> </>
            )}
          </MDBox>
        ),
        created: paylink.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("paylinks.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRateRows(result.data.data.paylinks);
            setPaylinks(rows);
            setCurrencies(result.data.data.currencies);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred.");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function issueCheque() {
    if (paylinkTitle === null) {
      setSeverity("error");
      setErrMsg("The title is required.");
      setOpen(true);
      return;
    }

    if (description === null) {
      setSeverity("error");
      setErrMsg("A description is required.");
      setOpen(true);
      return;
    }

    if (selectedCurrency === null || selectedCurrency === "") {
      setSeverity("error");
      setErrMsg("Currency is required.");
      setOpen(true);
      return;
    }

    if (paylinkAmount !== null && paylinkAmount < 0) {
      setSeverity("error");
      setErrMsg("Enter a valid amount.");
      setOpen(true);
      return;
    }

    if (feeBearer === null) {
      setSeverity("error");
      setErrMsg("Fee bearer is required");
      setOpen(true);
      return;
    }

    if (oneTime === null) {
      setSeverity("error");
      setErrMsg("Frequency is required");
      setOpen(true);
      return;
    }

    if (displayName !== null) {
      if (displayName.length < 2) {
        setSeverity("error");
        setErrMsg("Enter a valid display name");
        setOpen(true);
        return;
      }
    }

    if (payerEmail !== null || payer !== null) {
      if (payer === null) {
        setSeverity("error");
        setErrMsg("Payer's name is required");
        setOpen(true);
        return;
      }

      if (payerEmail === null) {
        setSeverity("error");
        setErrMsg("Payer's email is required");
        setOpen(true);
        return;
      }
    }

    setOpenNewCheckDialog(false);
    setShowLoading(true);
    const json = {
      currency: `${selectedCurrency}`,
      amount: paylinkAmount * 100,
      subject: paylinkTitle,
      fee_bearer: `${feeBearer}`,
      description: `${description}`,
      one_time: oneTime === "YES" ? 1 : 0,
      payer_name: payer,
      payer_email_address: payerEmail,
      display_name: displayName,
    };

    await axios
      .post("paylinks/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Pay Link created!");
          setOpen(true);

          // Reset Form
          setTitle(null);
          setAmount(null);
          setCurrency(null);
          setAmountLabel("Amount");
          setDisplayName(null);
          setDescription(null);
          setFeeBearer(null);
          setOneTime(null);
          setPayer(null);
          setPayerEmail(null);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openNewCheckDialog}>
        <DialogTitle>
          <MDBox>Create PayLink</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>
              Collect payments from anyone, anywhere in the world via their preferred means of
              payment while keeping your banking information private.
            </MDBox>
          </DialogContentText>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={3}>
              <MDInput
                name="title"
                margin="dense"
                label="Title"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setTitle(e.target.value)}
              />
            </MDBox>
            {/* <MDBox mt={2}>
              <MDInput
                multiline
                rows={3}
                name="description"
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setDescription(e.target.value)}
              />
            </MDBox> */}
            <MDBox mt={2}>
              <Editor
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description"
              />
            </MDBox>

            <MDBox mt={2} mb={1}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={6} lg={6}>
                  <MDBox mt={1}>
                    <FormControl sx={{ minWidth: 120, width: "100%" }}>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        label="Currency"
                        onChange={(e) => {
                          setCurrency(e.target.value);
                        }}
                        sx={{ padding: "12px", paddingLeft: "3px" }}
                      >
                        {
                          // --> Notice below changes
                          currencies.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                            currencies.map((record) => (
                              <MenuItem value={record.currency_code}>
                                {record.currency_code}
                              </MenuItem>
                            ))
                        }
                      </Select>
                    </FormControl>
                  </MDBox>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <MDBox>
                    <MDInput
                      name="amount"
                      margin="dense"
                      label={amountLabel}
                      type="number"
                      fullWidth
                      variant="outlined"
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </MDBox>
                </Grid>
              </Grid>
              <MDBox variant="caption" fontWeight="light" textAlign="center">
                <small>Leave the amount field blank or set to zero to accept any amount.</small>
              </MDBox>
            </MDBox>

            <MDBox mt={2} mb={1}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={6} lg={6}>
                  <MDBox mt={3}>
                    <FormControl sx={{ minWidth: 120, width: "100%" }}>
                      <InputLabel>Usage</InputLabel>
                      <Select
                        label="Usage"
                        onChange={(e) => {
                          setOneTime(e.target.value);
                        }}
                        sx={{ padding: "12px", paddingLeft: "3px" }}
                      >
                        <MenuItem value="YES">One Time</MenuItem>
                        <MenuItem value="NO">Forever</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                  <MDBox mt={3}>
                    <FormControl sx={{ minWidth: 120, width: "100%" }}>
                      <InputLabel>Fee Bearer</InputLabel>
                      <Select
                        label="Fee Bearer"
                        onChange={(e) => {
                          setFeeBearer(e.target.value);
                        }}
                        sx={{ padding: "12px", paddingLeft: "3px" }}
                      >
                        <MenuItem value="PAYER">Payer</MenuItem>
                        <MenuItem value="PAYEE">Self</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox mt={4}>
              <MDTypography variant="h6" fontWeight="light">
                Optional Information
              </MDTypography>
              <MDBox mt={1}>
                <MDInput
                  name="display_name"
                  margin="dense"
                  label="Display Name (Shown on pay link)"
                  type="text"
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </MDBox>
              <MDBox mt={2}>
                <MDInput
                  name="payer_name"
                  margin="dense"
                  label="Payer's Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setPayer(e.target.value)}
                />
              </MDBox>
              <MDBox mt={2}>
                <MDInput
                  name="payer_email"
                  margin="dense"
                  label="Payer's Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setPayerEmail(e.target.value)}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewCheckDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              issueCheque();
            }}
          >
            Create PayLink
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox>
                  <Card sx={{ height: "100%", marginTop: "0px" }}>
                    <MDBox p={3}>
                      <MDBox
                        pt={2}
                        px={2}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="h6" fontWeight="medium">
                          Pay Links
                        </MDTypography>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            // navigate("/payouts/create");
                            setOpenNewCheckDialog(true);
                          }}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                          &nbsp;new pay link
                        </MDButton>
                      </MDBox>
                      <MDBox mt={5}>
                        <DataTable
                          table={{ columns: tableColumns, rows: paylinks }}
                          isSorted={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PayLinks;
